import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
    Plus, KeyRound, Copy, ExternalLink, Edit, Trash2, Eye, EyeOff, 
    Search, Lock, Shield, User, Users, Share2, X
} from 'lucide-react';

const CATEGORY_ICONS = {
    allgemein: '📁',
    social_media: '📱',
    hosting: '🌐',
    email: '📧',
    analytics: '📊',
    ads: '📢',
    cms: '💻',
    cloud: '☁️',
    other: '📦'
};

const CATEGORY_LABELS = {
    allgemein: 'Allgemein',
    social_media: 'Social Media',
    hosting: 'Hosting & Domain',
    email: 'E-Mail',
    analytics: 'Analytics',
    ads: 'Werbung',
    cms: 'CMS / Website',
    cloud: 'Cloud Storage',
    other: 'Sonstiges'
};

const CustomerCredentials = ({ customer, onClose, embedded = false }) => {
    const { api, user } = useAuth();
    const [credentials, setCredentials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [editingCredential, setEditingCredential] = useState(null);
    const [sharingCredential, setSharingCredential] = useState(null);
    const [showPasswords, setShowPasswords] = useState({});
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        category: 'allgemein',
        username: '',
        email: '',
        password: '',
        url: '',
        notes: '',
        visible_to: []
    });

    const canManage = user?.role === 'chef' || user?.role === 'account_manager';

    useEffect(() => {
        fetchCredentials();
        fetchUsers();
    }, [customer.id]);

    const fetchCredentials = async () => {
        try {
            const res = await api.get(`/customers/${customer.id}/credentials`);
            setCredentials(res.data);
        } catch (error) {
            toast.error('Fehler beim Laden der Zugänge');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await api.get('/users');
            setUsers(res.data.filter(u => u.id !== user.id));
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const handleSave = async () => {
        try {
            if (editingCredential) {
                await api.put(`/customers/${customer.id}/credentials/${editingCredential.id}`, formData);
                toast.success('Zugang aktualisiert');
            } else {
                await api.post(`/customers/${customer.id}/credentials`, formData);
                toast.success('Zugang erstellt');
            }
            setShowModal(false);
            resetForm();
            fetchCredentials();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        }
    };

    const handleDelete = async (credentialId) => {
        if (!window.confirm('Zugang wirklich löschen?')) return;
        try {
            await api.delete(`/customers/${customer.id}/credentials/${credentialId}`);
            toast.success('Zugang gelöscht');
            fetchCredentials();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    const handleShare = async () => {
        if (!sharingCredential) return;
        try {
            await api.put(`/customers/${customer.id}/credentials/${sharingCredential.id}/visibility`, selectedUsers);
            toast.success('Freigabe aktualisiert');
            setShowShareModal(false);
            setSharingCredential(null);
            fetchCredentials();
        } catch (error) {
            toast.error('Fehler beim Aktualisieren der Freigabe');
        }
    };

    const openEdit = (cred) => {
        setEditingCredential(cred);
        setFormData({
            title: cred.title,
            category: cred.category,
            username: cred.username,
            email: cred.email,
            password: '',
            url: cred.url,
            notes: cred.notes,
            visible_to: cred.visible_to
        });
        setShowModal(true);
    };

    const openShare = (cred) => {
        setSharingCredential(cred);
        setSelectedUsers(cred.visible_to || []);
        setShowShareModal(true);
    };

    const resetForm = () => {
        setEditingCredential(null);
        setFormData({
            title: '',
            category: 'allgemein',
            username: '',
            email: '',
            password: '',
            url: '',
            notes: '',
            visible_to: []
        });
    };

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        toast.success(`${field} kopiert`);
    };

    const togglePassword = (id) => {
        setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleUserSelection = (userId) => {
        setSelectedUsers(prev => 
            prev.includes(userId) 
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    // Group credentials by category
    const groupedCredentials = credentials.reduce((acc, cred) => {
        const category = cred.category || 'allgemein';
        if (!acc[category]) acc[category] = [];
        if (cred.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            cred.username.toLowerCase().includes(searchQuery.toLowerCase())) {
            acc[category].push(cred);
        }
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    // Content to render (either in Dialog or standalone)
    const content = (
        <>
            {/* Header */}
            <div className="flex items-center justify-between p-4 pr-12 border-b">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <KeyRound className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold">Zugänge - {customer.name}</h3>
                        <p className="text-sm text-muted-foreground">
                            {credentials.length} Zugang{credentials.length !== 1 ? 'e' : ''} gespeichert
                        </p>
                    </div>
                </div>
                {canManage && (
                    <Button onClick={() => { resetForm(); setShowModal(true); }} size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Zugang hinzufügen
                    </Button>
                )}
            </div>

            {/* Search */}
            <div className="p-4 border-b">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Zugänge suchen..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                    />
                </div>
            </div>

            {/* Credentials List */}
            <ScrollArea className={embedded ? "max-h-[55vh]" : "max-h-[60vh]"}>
                <div className="p-4 space-y-6">
                    {Object.entries(groupedCredentials).map(([category, creds]) => (
                        creds.length > 0 && (
                            <div key={category}>
                                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                                    <span>{CATEGORY_ICONS[category]}</span>
                                    {CATEGORY_LABELS[category]}
                                    <Badge variant="secondary" className="text-xs">{creds.length}</Badge>
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {creds.map(cred => (
                                        <motion.div
                                            key={cred.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                        >
                                            <Card className={`shadow-soft ${!cred.is_visible ? 'opacity-60' : ''}`}>
                                                <CardHeader className="pb-2 pt-4 px-4">
                                                    <div className="flex items-start justify-between">
                                                        <div>
                                                            <CardTitle className="text-base flex items-center gap-2">
                                                                {cred.title}
                                                                {!cred.is_visible && (
                                                                    <Lock className="w-3 h-3 text-muted-foreground" />
                                                                )}
                                                            </CardTitle>
                                                            <p className="text-xs text-muted-foreground">
                                                                Erstellt von {cred.created_by_name}
                                                            </p>
                                                        </div>
                                                        {(user?.role === 'chef' || cred.created_by === user?.id) && (
                                                            <div className="flex gap-1">
                                                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openShare(cred)} title="Freigabe verwalten">
                                                                    <Share2 className="w-3.5 h-3.5" />
                                                                </Button>
                                                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(cred)}>
                                                                    <Edit className="w-3.5 h-3.5" />
                                                                </Button>
                                                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(cred.id)}>
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </CardHeader>
                                                <CardContent className="px-4 pb-4 space-y-2">
                                                    {cred.username && (
                                                        <div className="flex items-center justify-between p-2 rounded bg-muted/50 text-sm">
                                                            <div className={!cred.is_visible ? 'blur-sm select-none' : ''}>
                                                                <span className="text-muted-foreground text-xs">User: </span>
                                                                <span className="font-mono">{cred.username}</span>
                                                            </div>
                                                            {cred.is_visible && (
                                                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(cred.username, 'Benutzername')}>
                                                                    <Copy className="w-3 h-3" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}
                                                    {cred.email && (
                                                        <div className="flex items-center justify-between p-2 rounded bg-muted/50 text-sm">
                                                            <div className={!cred.is_visible ? 'blur-sm select-none' : ''}>
                                                                <span className="text-muted-foreground text-xs">E-Mail: </span>
                                                                <span className="font-mono">{cred.email}</span>
                                                            </div>
                                                            {cred.is_visible && (
                                                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(cred.email, 'E-Mail')}>
                                                                    <Copy className="w-3 h-3" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="flex items-center justify-between p-2 rounded bg-muted/50 text-sm">
                                                        <div className={!cred.is_visible ? 'blur-sm select-none' : ''}>
                                                            <span className="text-muted-foreground text-xs">Passwort: </span>
                                                            <span className="font-mono">
                                                                {cred.is_visible 
                                                                    ? (showPasswords[cred.id] ? cred.password : '••••••••')
                                                                    : '••••••••'
                                                                }
                                                            </span>
                                                        </div>
                                                        {cred.is_visible && (
                                                            <div className="flex gap-1">
                                                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => togglePassword(cred.id)}>
                                                                    {showPasswords[cred.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                                                </Button>
                                                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(cred.password, 'Passwort')}>
                                                                    <Copy className="w-3 h-3" />
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {cred.url && cred.is_visible && (
                                                        <Button 
                                                            variant="outline" 
                                                            size="sm"
                                                            className="w-full h-8 text-xs"
                                                            onClick={() => window.open(cred.url.startsWith('http') ? cred.url : `https://${cred.url}`, '_blank')}
                                                        >
                                                            <ExternalLink className="w-3 h-3 mr-1" />
                                                            Öffnen
                                                        </Button>
                                                    )}
                                                    {cred.visible_to?.length > 0 && (
                                                        <div className="flex items-center gap-1 pt-1">
                                                            <Users className="w-3 h-3 text-muted-foreground" />
                                                            <span className="text-xs text-muted-foreground">
                                                                Geteilt mit {cred.visible_to.length} Person{cred.visible_to.length !== 1 ? 'en' : ''}
                                                            </span>
                                                        </div>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        )
                    ))}

                    {credentials.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground">
                            <KeyRound className="w-12 h-12 mx-auto mb-4 opacity-20" />
                            <p>Noch keine Zugänge für diesen Kunden</p>
                            {canManage && (
                                <Button variant="outline" className="mt-4" onClick={() => setShowModal(true)}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Ersten Zugang hinzufügen
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </ScrollArea>

            {/* Create/Edit Modal */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-primary" />
                            {editingCredential ? 'Zugang bearbeiten' : 'Neuer Zugang'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Titel *</Label>
                                <Input
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="z.B. Instagram Business"
                                />
                            </div>
                            <div>
                                <Label>Kategorie</Label>
                                <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                                            <SelectItem key={key} value={key}>
                                                <span className="flex items-center gap-2">
                                                    {CATEGORY_ICONS[key]} {label}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Benutzername</Label>
                                <Input
                                    value={formData.username}
                                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label>E-Mail</Label>
                                <Input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Passwort {!editingCredential && '*'}</Label>
                            <Input
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                placeholder={editingCredential ? 'Leer lassen für unverändert' : ''}
                            />
                        </div>
                        <div>
                            <Label>URL</Label>
                            <Input
                                value={formData.url}
                                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                                placeholder="https://..."
                            />
                        </div>
                        <div>
                            <Label>Notizen</Label>
                            <Input
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Zusätzliche Informationen"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowModal(false)}>Abbrechen</Button>
                        <Button onClick={handleSave} disabled={!formData.title || (!editingCredential && !formData.password)}>
                            {editingCredential ? 'Aktualisieren' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Share Modal */}
            <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Share2 className="w-5 h-5 text-primary" />
                            Freigabe verwalten
                        </DialogTitle>
                        <DialogDescription>
                            Wähle, wer "{sharingCredential?.title}" sehen darf
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {users.map(u => (
                                <div 
                                    key={u.id}
                                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                                        selectedUsers.includes(u.id) ? 'bg-primary/10' : 'hover:bg-muted/50'
                                    }`}
                                    onClick={() => toggleUserSelection(u.id)}
                                >
                                    <Checkbox checked={selectedUsers.includes(u.id)} />
                                    <Avatar className="w-8 h-8">
                                        {u.profile_image && <AvatarImage src={u.profile_image} />}
                                        <AvatarFallback style={{ backgroundColor: u.profile_color || '#009dde' }} className="text-white text-xs">
                                            {u.first_name?.[0]}{u.last_name?.[0]}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium">{u.first_name} {u.last_name}</p>
                                        <p className="text-xs text-muted-foreground capitalize">{u.role}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-4">
                            <Shield className="w-3 h-3 inline mr-1" />
                            Admin sieht immer alle Zugänge. Der Ersteller hat immer Zugriff.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowShareModal(false)}>Abbrechen</Button>
                        <Button onClick={handleShare}>
                            Freigabe speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );

    // If embedded, render content directly; otherwise wrap in Dialog
    if (embedded) {
        return <div className="bg-card rounded-lg border">{content}</div>;
    }

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
                {content}
            </DialogContent>
        </Dialog>
    );
};

export default CustomerCredentials;
